/**
 * The section overviews (`/overview/:sectionId`): what happened in the section lately, and what
 * is waiting on the dealer. One response per section, scoped to the selected org node.
 *
 * Mirrors `backend/src/modules/section-overview/section-overview.types.ts`. A figure the user
 * may not open the list behind is left out of the response, so everything here may be shown.
 */

export type SectionId = 'tyres' | 'service-agreement' | 'system';

export const SECTION_IDS: readonly SectionId[] = ['tyres', 'service-agreement', 'system'];

export function isSectionId(value: string): value is SectionId {
  return (SECTION_IDS as readonly string[]).includes(value);
}

export type KpiId =
  | 'tyre-orders'
  | 'open-tyre-leads'
  | 'specs-without-offers'
  | 'agreement-orders'
  | 'active-agreements'
  | 'over-km'
  | 'open-agreement-leads'
  | 'import-runs'
  | 'failed-runs'
  | 'mautic-errors'
  | 'unmatched-vehicles';

export type IssueKind =
  | 'tyre-specs-without-offers'
  | 'open-tyre-leads'
  | 'over-km'
  | 'agreements-expiring'
  | 'open-agreement-leads'
  | 'csv-upload-stale'
  | 'csv-run-failed'
  | 'mautic-sync-failed'
  | 'unmatched-vehicles';

export type IssueSeverity = 'critical' | 'warning' | 'info';

/** A headline number. `previous` is the period before; null when the number is a current state. */
export interface OverviewKpi {
  id: KpiId;
  value: number;
  previous: number | null;
  /** Higher is better (orders), or worse (open issues): colours the change. */
  better: 'up' | 'down';
  /** Where the number's list lives. */
  href?: string;
  /** Set when the number itself means something is wrong. */
  severity?: IssueSeverity;
}

export interface IssueDetail {
  id: string;
  /** The thing's own name: a dimension, a reg.nr., a customer, a dealer, a file name. */
  label: string;
  /** The issue kind's per-row figure, worded by `issueDetail` (tyres, km over). */
  count?: number;
  /** ISO 8601. */
  at?: string;
  /** For a tyre spec. ALL_SEASON never reaches the frontend. */
  season?: 'SUMMER' | 'WINTER';
}

/** Something that needs an action. Sorted by the backend: severity, then count. */
export interface OverviewIssue {
  kind: IssueKind;
  severity: IssueSeverity;
  count: number;
  href?: string;
  /** The first few affected rows; `count` is the total. */
  details: IssueDetail[];
}

/** Per-day counts across the period, oldest first. */
export interface OverviewTrend {
  id: 'tyre-orders' | 'agreement-orders' | 'import-runs';
  points: { date: string; value: number }[];
}

export interface SectionOverview {
  sectionId: SectionId;
  /** ISO 8601. */
  generatedAt: string;
  periodDays: number;
  kpis: OverviewKpi[];
  issues: OverviewIssue[];
  trend: OverviewTrend | null;
}
